import { memo, useCallback, useMemo } from 'react';
import { Obstacle } from '@thive/fddm-types';

import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { SelectInput } from '@/modules/common/components/inputs';
import { IFddmService } from '@/modules/canvas/services/Floorplan/IFddmService';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { getCommonOption } from '@/modules/common/helpers/commonValue';
import { DisabledReasonTooltip } from '../common/DisabledReasonTooltip';
import { useTranslatedOptions } from '@/modules/common/hooks';

const WALL_MATERIAL_OPTIONS: { labelKey: string; value: Obstacle['wallMaterial'] }[] = [
  { labelKey: 'interface:properties.wall.material_options.stone', value: 'STONE' },
  { labelKey: 'interface:properties.wall.material_options.brick', value: 'BRICK' },
  { labelKey: 'interface:properties.wall.material_options.concrete', value: 'CONCRETE' },
];

interface Props {
  selectedWalls: Obstacle[];
}

function WallMaterialComponent({ selectedWalls }: Props) {
  const fddmSvc: IFddmService = useMemo(
    () => FddmServiceFactory.getInstance().createFddmService(),
    [],
  );

  const options = useTranslatedOptions(WALL_MATERIAL_OPTIONS);

  // Shown when any selected wall has a material, only editable when all of them do
  const wallMaterials = useMemo(
    () => selectedWalls.flatMap(({ wallMaterial }) => (wallMaterial ? [wallMaterial] : [])),
    [selectedWalls],
  );
  const isPartialSelection = wallMaterials.length !== selectedWalls.length;

  const wallMaterial = useMemo(() => getCommonOption(wallMaterials), [wallMaterials]);

  const onChange = useCallback(
    (newValue: Obstacle['wallMaterial']) => {
      if (isPartialSelection) return;

      fddmSvc.setPreference('obstacle.wallMaterial', newValue);
      fddmSvc.updateObstacles(selectedWalls.map((wall) => ({ ...wall, wallMaterial: newValue })));
    },
    [isPartialSelection, selectedWalls, fddmSvc],
  );

  if (wallMaterials.length === 0) return null;

  return (
    <PropertiesLabel i18nkey='interface:properties.wall.material'>
      <DisabledReasonTooltip
        reasonKey={
          isPartialSelection
            ? 'interface:properties.wall.partial_selection.only_with_material'
            : undefined
        }
      >
        <SelectInput
          disabled={isPartialSelection}
          onChange={onChange}
          options={options}
          value={wallMaterial}
          showMixed
        />
      </DisabledReasonTooltip>
    </PropertiesLabel>
  );
}

const WallMaterial = memo(WallMaterialComponent);

export default WallMaterial;
